import { useContext, useCallback } from 'react'
import { jwtDecode } from 'jwt-decode'
import { AppContext } from '../context/AppProvider'
import { loginUserAction, logoutUserAction, updateUserProfileAction } from '../actions/userActions'
import { useCart } from './useCart'
import { useFavorites } from './useFavorites'
import { buildPublicApiUrl } from '../config/api'

export const useUser = () => {
  const { dispatch } = useContext(AppContext)
  const { getCartItems, resetCartState } = useCart()
  const { getFavorites, resetFavoritesState } = useFavorites()

  const handleUserLogout = useCallback(() => {
    localStorage.removeItem('token')
    dispatch(logoutUserAction())
    resetCartState()
    resetFavoritesState()
  }, [dispatch, resetCartState, resetFavoritesState])

  const getUserData = useCallback(async (token, userId) => {
    let headers = { 'Content-Type': 'application/json' }
    if (token) {
      headers = { ...headers, Authorization: `${token}` }
      try {
        const url = buildPublicApiUrl(`users/${userId}`)
        const response = await fetch(url, {
          method: 'GET',
          headers
        })
        if (response.ok) {
          const resp = await response.json()
          const { data } = resp
          dispatch(loginUserAction(data))
        } else {
          console.error('Error with status code:', response.status)
          handleUserLogout()
        }
      } catch (error) {
        console.error('Login error:', error)
      }
    }
  }, [dispatch, handleUserLogout])

  const handleUserAuthentication = useCallback(async (token) => {
    try {
      const userData = jwtDecode(token)
      await getUserData(token, userData.user_id)
      await getCartItems(1)
      await getFavorites(1)
    } catch (error) {
      console.error('Token handling failed:', error)
      handleUserLogout()
    }
  }, [getUserData, getCartItems, getFavorites, handleUserLogout])

  const handleDeleteUser = useCallback(async () => {
    const token = localStorage.getItem('token')
    if (token) {
      try {
        const userData = jwtDecode(token)
        deleteUser(token, userData.user_id)
        resetCartState()
        resetFavoritesState()
      } catch (error) {
        console.error('Token handling failed:', error)
        handleUserLogout()
      }
    }
  }, [resetCartState, resetFavoritesState, handleUserLogout])

  const handleUpdateUserProfile = async (payload) => {
    const token = localStorage.getItem('token')
    if (token) {
      try {
        const userData = jwtDecode(token)
        return await updateUserProfile(token, userData.user_id, payload)
      } catch (error) {
        console.error('Token handling failed:', error)
        throw error
      }
    }
    throw new Error('Unauthorized')
  }

  const loginUser = async ({ email, password, turnsitleToken }) => {
    try {
      const response = await fetch(buildPublicApiUrl('login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password, turnsitleToken })
      })
      const { token, message } = await response.json()
      if (token) {
        localStorage.setItem('token', token)
        await handleUserAuthentication(token, dispatch)
      }
      return message
    } catch (error) {
      console.error('Login error:', error)
    }
  }

  const logoutUser = () => {
    localStorage.removeItem('token')
    handleUserLogout()
  }

  const registerUser = async ({ ...data }) => {
    try {
      const response = await fetch(buildPublicApiUrl('register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })
      let payload = {}
      try {
        payload = await response.json()
      } catch (parseError) {
        payload = {}
      }

      const message = payload?.message || (response.ok
        ? 'Înregistrarea a fost procesată.'
        : `Înregistrarea a eșuat (${response.status}).`)
      const messageType = payload?.messageType || (response.ok ? 'info' : 'error')

      return { message, messageType, statusCode: response.status }
    } catch (error) {
      console.error('Registration error:', error)
      return {
        message: 'Înregistrarea a eșuat din cauza unei erori de rețea.',
        messageType: 'error',
        error
      }
    }
  }

  const requestPasswordReset = async ({ email }) => {
    try {
      const response = await fetch(buildPublicApiUrl('password-reset/request'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      })
      const payload = await response.json()
      return {
        ok: response.ok,
        message: payload?.message || 'Cererea a fost procesată.'
      }
    } catch (error) {
      console.error('Password reset request error:', error)
      return {
        ok: false,
        message: 'Cererea de resetare a eșuat din cauza unei erori de rețea.'
      }
    }
  }

  const confirmPasswordReset = async ({ token, password }) => {
    try {
      const response = await fetch(buildPublicApiUrl('password-reset/confirm'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token, password })
      })
      const payload = await response.json()
      return {
        ok: response.ok,
        message: payload?.message || 'Cererea a fost procesată.'
      }
    } catch (error) {
      console.error('Password reset confirm error:', error)
      return {
        ok: false,
        message: 'Resetarea parolei a eșuat din cauza unei erori de rețea.'
      }
    }
  }

  const deleteUser = async (token, userId) => {
    let headers = { 'Content-Type': 'application/json' }
    if (token) {
      headers = { ...headers, Authorization: `${token}` }
      try {
        const url = buildPublicApiUrl(`users/${userId}`)
        const response = await fetch(url, {
          method: 'DELETE',
          headers
        })
        const { data } = await response.json()
        dispatch(logoutUser(data))
      } catch (error) {
        console.error('Login error:', error)
      }
    }
  }

  const updateUserProfile = async (token, userId, payload) => {
    let headers = { 'Content-Type': 'application/json' }
    if (token) {
      headers = { ...headers, Authorization: `${token}` }
      try {
        const url = buildPublicApiUrl(`users/${userId}`)
        const response = await fetch(url, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ data: payload })
        })
        const data = await response.json()
        if (!response.ok) {
          throw new Error(data?.message || 'Profile update failed.')
        }
        if (data?.data) {
          dispatch(updateUserProfileAction(data.data))
        } else {
          dispatch(updateUserProfileAction(payload))
        }
        return data?.message || 'Document successfully updated.'
      } catch (error) {
        console.error('Login error:', error)
        throw error
      }
    }
    throw new Error('Unauthorized')
  }

  return {
    loginUser,
    logoutUser,
    registerUser,
    requestPasswordReset,
    confirmPasswordReset,
    handleUserAuthentication,
    handleUserLogout,
    handleDeleteUser,
    handleUpdateUserProfile
  }
}
